<script lang="ts" setup>
import type { ButtonColor } from '@/components/button/SkrButton.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import SkrDivider from '@/components/SkrDivider.vue'

interface Props {
  title: string
  maxWidth?: string
  actionText?: string
  actionColor?: ButtonColor
  cancelText?: string
  disabled?: boolean
  loading?: boolean
  persistent?: boolean
  dataCy?: string
  divider?: boolean
  centered?: boolean
  // TODO: once we refactor wave to use the new dialog, remove this prop and associated styles. We should implement a new prop that allows more flexible padding/margin for each dialog or each dialog should be designed with the same padding/margin
  refreshed?: boolean
}

withDefaults(defineProps<Props>(), {
  maxWidth: '570',
})

const emit = defineEmits<{
  close: []
  action: []
}>()

const { t } = useI18n()

const modelValue = ref(true)

watchEffect(() => {
  if (!modelValue.value) {
    emit('close')
  }
})
</script>

<template>
  <!-- scroll-strategy on v-dialog set to 'none' because of a bug in v-dialog: the v-overlay inside v-dialog is not mounted on time 
   before the dialog tries to access scrolling behavior on the overlay  -->
  <v-dialog
    v-model="modelValue"
    :max-width="maxWidth"
    :persistent="persistent"
    :close-on-back="true"
    scroll-strategy="none"
  >
    <div
      class="base-dialog"
      :class="{
        'base-dialog--with-divider': divider,
        'base-dialog--center': centered,
        'base-dialog--refreshed': refreshed,
      }"
      :data-cy="dataCy"
    >
      <h2 class="base-dialog__title">
        {{ title }}
      </h2>
      <div class="base-dialog__content">
        <slot />
      </div>
      <slot name="actions">
        <skr-divider v-if="divider" class="base-dialog__divider" />
        <div class="base-dialog__actions">
          <skr-button data-cy="dialog_cancel_button" type="secondary" size="xl" @click="emit('close')">
            {{ cancelText ?? t('global.no_thanks') }}
          </skr-button>
          <skr-button
            data-cy="dialog_action_button"
            size="xl"
            :color="actionColor"
            :disabled="disabled"
            :loading="loading"
            @click="emit('action')"
          >
            {{ actionText ?? t('global.accept') }}
          </skr-button>
        </div>
      </slot>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.base-dialog {
  padding: 9 * $bw 12 * $bw;
  background-color: $c-white;
  border-radius: 30px;
  &__title {
    color: $c-grey-darker;
    margin-top: 4 * $bw;
    margin-bottom: 4 * $bw;
    white-space: normal;
  }
  &__divider {
    margin: 9 * $bw -12 * $bw 0;
  }
  &__actions {
    margin-top: 12 * $bw;
    display: flex;
    justify-content: flex-end;
    gap: 4 * $bw;
  }
  &--with-divider &__actions {
    margin-top: 6 * $bw;
  }
  // `centered` centers the whole dialog — title, content and both buttons.
  &--center &__title,
  &--center &__content {
    text-align: center;
  }
  &--center &__actions {
    justify-content: center;
  }

  &--refreshed {
    padding-top: 0;
    padding-bottom: 6 * $bw;
    border-radius: 16px;
  }
  &--refreshed &__title {
    margin-top: 12 * $bw;
  }
  &--refreshed#{&}--center {
    padding-bottom: 12 * $bw;
  }
}
</style>
